// Designer wrapper around the shared transaction form
// (@twasta/page-runtime/txn). It injects the designer TxnRuntime (live
// preview endpoints + document store + toast) so every existing call site
// keeps working unchanged. The actual form lives in the shared package and
// is the SAME component the standalone runtime renders.

import { useMemo } from 'react'
import {
  FormRenderer as CoreFormRenderer, TxnRuntimeProvider, fieldAnchorId,
  type UploadResult, type RunActionResponse, type TxnRuntime,
} from '@twasta/page-runtime/txn'
import type { SqlModel } from '@/pages/designers/transaction/types'
import { makeDesignerTxnRuntime } from './designerTxnRuntime'

export { fieldAnchorId }

interface Props {
  model: SqlModel
  values: Record<string, unknown>
  errors: Record<string, string>
  onChange: (db_name: string, value: unknown) => void
  readOnlyKey: boolean
  // View-only: record locked (designer rule / awaiting approval) or no edit
  // rights. Forwarded to the shared form (all fields disabled, action bar
  // hidden, View indicator). ``lockReason`` is shown beside the indicator.
  viewOnly?: boolean
  lockReason?: string
  mode?: 'add' | 'edit' | 'view'
  project: string
  objName: string
  headerValues?: Record<string, unknown>
  // Runtime per-column protect override from an item_change return.
  dynamicProtect?: Record<string, 0 | 1>
  // Per-call upload override (deployed-site embeds inject site-doc upload).
  // Folded into the injected TxnRuntime; live preview uses the default
  // document-store uploader.
  uploadDocument?: (file: File) => Promise<UploadResult>
  // Surface-aware run_action transport (deployed-site embeds route to
  // /site-txn). Folded into the injected runtime — NOT a CoreFormRenderer prop.
  runAction?: TxnRuntime['runAction']
  // ── Custom action buttons (forwarded verbatim to the shared form) ───────
  formNo?: string
  txnId?: string
  parentPk?: Record<string, unknown>
  onActionResult?: (res: RunActionResponse) => void
  onSystemAction?: (op: 'add' | 'edit' | 'delete' | 'save' | 'view') => void
  onActionError?: (err: unknown) => void
  // Ran-and-committed hook: hosts showing one persisted record re-read it here,
  // since the action's business logic may have changed the record itself.
  onActionDone?: (res: RunActionResponse) => void
}

export default function FormRenderer({ uploadDocument, runAction, ...rest }: Props) {
  const runtime = useMemo(
    () => makeDesignerTxnRuntime(rest.project, uploadDocument, runAction),
    [rest.project, uploadDocument, runAction],
  )
  return (
    <TxnRuntimeProvider value={runtime}>
      <CoreFormRenderer {...rest} />
    </TxnRuntimeProvider>
  )
}
